import { apiClient } from './api';
import { HomeBase, ShortestPath, SetHomeBaseRequest } from '../types/homeBase';

const DEFAULT_CRUISE_SPEED_MS = 10;

export const homeBaseService = {
  /**
   * Fetch the home base for a drone.
   *
   * A drone with no home base configured resolves normally, with
   * `is_set: false` — that is the state every drone starts in, not a failure
   * (#633). Throws with status 404 only when the drone itself is not found or
   * not visible to this operator.
   */
  async getHomeBase(droneId: string): Promise<HomeBase> {
    try {
      return await apiClient.get<HomeBase>(`/api/v1/drones/${droneId}/home`);
    } catch (error) {
      console.error(`Failed to fetch home base for drone ${droneId}:`, error);
      throw error;
    }
  },

  /**
   * Set (or replace) the home base for a drone.
   * Returns the saved HomeBase on success.
   */
  async setHomeBase(
    droneId: string,
    request: SetHomeBaseRequest
  ): Promise<HomeBase> {
    try {
      return await apiClient.put<HomeBase>(
        `/api/v1/drones/${droneId}/home`,
        request
      );
    } catch (error) {
      console.error(`Failed to set home base for drone ${droneId}:`, error);
      throw error;
    }
  },

  /**
   * Fetch the shortest path from the drone's current position to its home base.
   * Throws with status 409 when HOME_NOT_SET or DRONE_POSITION_UNKNOWN,
   * or 404 when DRONE_NOT_FOUND.
   */
  async getShortestPathToHome(
    droneId: string,
    cruiseSpeedMs: number = DEFAULT_CRUISE_SPEED_MS
  ): Promise<ShortestPath> {
    try {
      return await apiClient.get<ShortestPath>(
        `/api/v1/drones/${droneId}/shortest-path-to-home?cruise_speed_ms=${cruiseSpeedMs}`
      );
    } catch (error) {
      console.error(
        `Failed to fetch shortest path for drone ${droneId}:`,
        error
      );
      throw error;
    }
  },
};
